import React, { useState, useEffect } from 'react';
import PageHeader from '../components/PageHeader';
import './ContactPage.css';

// Declare global interface for reCAPTCHA
declare global {
  interface Window {
    grecaptcha?: any;
    ___grecaptcha_cfg?: any;
  }
}

// Formspark form ID - replace with your actual Formspark form ID
const FORMSPARK_FORM_ID = "I79QAdcaq";

// Google reCAPTCHA site key - replace with your actual site key
const RECAPTCHA_SITE_KEY = "6LfMWkwrAAAAAI6F727wZC1V2rmYX-nWKim2zbKn";

const ContactPage: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    purpose: ''
  });
  
  // Track if we need to reload reCAPTCHA
  const [shouldReloadCaptcha, setShouldReloadCaptcha] = useState(true);
  
  // Load reCAPTCHA script dynamically
  useEffect(() => {
    if (shouldReloadCaptcha) {
      // Clean up existing reCAPTCHA instances
      // First, remove any existing reCAPTCHA scripts
      const existingScripts = document.querySelectorAll('script[src*="recaptcha/api.js"]');
      existingScripts.forEach(script => script.remove());
      
      // Remove any global reCAPTCHA objects
      if (window.grecaptcha) {
        window.grecaptcha = undefined;
      }
      
      // Clean up any existing g-recaptcha DOM elements completely
      const recaptchaContainer = document.querySelector('.recaptcha-container');
      if (recaptchaContainer) {
        // Remove all g-recaptcha elements to ensure a clean state
        const oldWidgets = recaptchaContainer.querySelectorAll('.g-recaptcha');
        oldWidgets.forEach(widget => widget.remove());
        
        // Create a completely empty g-recaptcha div
        const freshWidget = document.createElement('div');
        freshWidget.className = 'g-recaptcha';
        freshWidget.dataset.sitekey = RECAPTCHA_SITE_KEY;
        
        // Add the widget before the note
        const note = recaptchaContainer.querySelector('.recaptcha-note');
        if (note) {
          recaptchaContainer.insertBefore(freshWidget, note);
        } else {
          recaptchaContainer.appendChild(freshWidget);
        }
        
        // Add a separate accessibility label that is NOT inside the g-recaptcha div
        // This ensures the g-recaptcha div remains empty for the API to initialize
        const accessibilityLabel = document.createElement('label');
        accessibilityLabel.className = 'sr-only';
        accessibilityLabel.setAttribute('for', 'g-recaptcha-response');
        accessibilityLabel.textContent = 'reCAPTCHA response';
        recaptchaContainer.insertBefore(accessibilityLabel, freshWidget);
      }
      
      // Add the script with a small delay to ensure DOM is ready
      setTimeout(() => {
        const script = document.createElement('script');
        script.src = 'https://www.google.com/recaptcha/api.js';
        script.async = true;
        script.defer = true;
        document.body.appendChild(script);
        
        // Reset the flag
        setShouldReloadCaptcha(false);
      }, 100);
    }
  }, [shouldReloadCaptcha]);  // Depend on shouldReloadCaptcha
  
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitStatus, setSubmitStatus] = useState<{
    submitted: boolean;
    success: boolean;
    message: string;
  }>({
    submitted: false,
    success: false,
    message: ''
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when field is edited
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    }
    
    const recaptchaResponse = getRecaptchaResponse();
    if (!recaptchaResponse) {
      newErrors.recaptcha = 'Please complete the reCAPTCHA verification';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Function to get reCAPTCHA response
  const getRecaptchaResponse = (): string | null => {
    // Safely check if grecaptcha is loaded and get response
    if (typeof window !== 'undefined' && 
        typeof (window as any).grecaptcha !== 'undefined' && 
        typeof (window as any).grecaptcha.getResponse === 'function') {
      return (window as any).grecaptcha.getResponse() || null;
    }
    return null;
  };
  
  // Function to reset reCAPTCHA
  const resetRecaptcha = () => {
    console.log('Triggering reCAPTCHA reload');
    // Force immediate DOM removal
    const recaptchaContainer = document.querySelector('.recaptcha-container');
    if (recaptchaContainer) {
      const existingWidgets = recaptchaContainer.querySelectorAll('.g-recaptcha');
      existingWidgets.forEach(widget => widget.remove());
      
      // Create a fresh container
      const freshWidget = document.createElement('div');
      freshWidget.className = 'g-recaptcha';
      freshWidget.dataset.sitekey = RECAPTCHA_SITE_KEY;
      freshWidget.setAttribute('aria-label', 'reCAPTCHA challenge');
      
      // Create a custom label for accessibility
      const accessibilityLabel = document.createElement('label');
      accessibilityLabel.className = 'sr-only';
      accessibilityLabel.setAttribute('for', 'g-recaptcha-response');
      accessibilityLabel.textContent = 'reCAPTCHA response';
      
      // Add to DOM before any other elements in the container
      const firstChild = recaptchaContainer.firstChild;
      if (firstChild) {
        recaptchaContainer.insertBefore(freshWidget, firstChild);
      } else {
        recaptchaContainer.appendChild(freshWidget);
      }
    }
    
    // Remove existing scripts
    const existingScripts = document.querySelectorAll('script[src*="recaptcha/api.js"]');
    existingScripts.forEach(script => script.remove());
    
    // Add fresh script
    const script = document.createElement('script');
    script.src = 'https://www.google.com/recaptcha/api.js';
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);
    
    // Add observer to add titles to iframes created by reCAPTCHA
    setTimeout(() => {
      const mutationObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'childList') {
            const iframes = document.querySelectorAll('iframe:not([title])');
            iframes.forEach(iframe => {
              iframe.setAttribute('title', 'reCAPTCHA challenge frame');
            });
            
            // Add label to any generated textareas
            const recaptchaTextareas = document.querySelectorAll('textarea.g-recaptcha-response');
            recaptchaTextareas.forEach(textarea => {
              textarea.setAttribute('aria-label', 'reCAPTCHA response field');
              textarea.setAttribute('title', 'reCAPTCHA response');
            });
          }
        });
      });
      
      mutationObserver.observe(document.body, {
        childList: true,
        subtree: true
      });
    }, 1000);
  };

  const handleTryAgain = () => {
    // Reset the form status
    setSubmitStatus({
      submitted: false,
      success: false,
      message: ''
    });
    
    // Reset any form errors
    setErrors({});
    
    // Directly reset reCAPTCHA using our more direct method
    resetRecaptcha();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (validate()) {
      setSubmitStatus({
        submitted: true,
        success: false,
        message: 'Sending your message...'
      });
      
      // Get reCAPTCHA response before submission
      const recaptchaResponse = getRecaptchaResponse();
      if (!recaptchaResponse) {
        setSubmitStatus({
          submitted: true,
          success: false,
          message: 'Please complete the reCAPTCHA verification'
        });
        setErrors(prev => ({
          ...prev,
          recaptcha: 'Please complete the reCAPTCHA verification'
        }));
        return;
      }

      try {
        console.log('Submitting form to Formspark with ID:', FORMSPARK_FORM_ID);
        console.log('Form data:', { ...formData, recaptcha: 'token_present' });
        
        const response = await fetch(`https://api.formspark.io/${FORMSPARK_FORM_ID}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            ...formData,
            "g-recaptcha-response": recaptchaResponse
          })
        });
        
        console.log('Response status:', response.status);
        
        // Regardless of what the response body is, if we get a 200 OK, consider it a success
        // Formspark typically returns a 200 with the submitted data
        if (response.ok) {
          console.log('Form submitted successfully');
          
          // Success response
          setSubmitStatus({
            submitted: true,
            success: true,
            message: 'Your message has been sent! We will get back to you as soon as possible.'
          });
          
          // Reset form
          setFormData({
            name: '',
            email: '',
            phone: '',
            message: '',
            purpose: ''
          });
          
          // First reset the form status and then reset the reCAPTCHA using our direct method
          setTimeout(() => {
            handleTryAgain();
          }, 300);
        } else {
          // Error response
          const errorMessage = 'There was an error sending your message. Please try again.';
          
          console.log('Error submitting form:', errorMessage);
          setSubmitStatus({
            submitted: true,
            success: false,
            message: errorMessage
          });
          
          // Also reset reCAPTCHA when there's an error
          resetRecaptcha();
        }
      } catch (error) {
        console.error('Error submitting form:', error);
        setSubmitStatus({
          submitted: true,
          success: false,
          message: 'There was an error sending your message. Please try again.'
        });
        
        // Also reset reCAPTCHA when there's an error
        resetRecaptcha();
      }
    }
  };

  return (
    <div className="contact-page">
      <PageHeader
        title="Contact Us"
        description="Have a question or want to place an order? Fill out the form below and we'll get back to you as soon as possible."
      />
      
      <div className="container">
        
        <div className="contact-container">
          <div className="contact-info">
            <div className="info-card">
              <h3>Let's Connect</h3>
              <p>We'd love to hear from you! Fill out the form and we'll get back to you as soon as possible.</p>
              
              <div className="contact-highlights">
                <div className="highlight-item">
                  <div className="highlight-icon">✓</div>
                  <div className="highlight-text">Custom orders for any occasion</div>
                </div>
                
                <div className="highlight-item">
                  <div className="highlight-icon">✓</div>
                  <div className="highlight-text">Business and corporate catering</div>
                </div>
                
                <div className="highlight-item">
                  <div className="highlight-icon">✓</div>
                  <div className="highlight-text">Seasonal specialties and limited editions</div>
                </div>
              </div>
              
              <div className="contact-cta">
                <h4>Ready to make your event special?</h4>
                <p>Tell us about your needs, and we'll help create the perfect treats for your occasion.</p>
              </div>
            </div>
          </div>
          
          <div className="contact-form-container">
            {submitStatus.submitted ? (
              <div className={`success-message ${submitStatus.success ? 'success' : 'error'}`}>
                <h2>{submitStatus.success ? 'Thank You!' : 'Message Status'}</h2>
                <p>{submitStatus.message}</p>
                {submitStatus.success ? (
                  <button 
                    onClick={() => setSubmitStatus({ submitted: false, success: false, message: '' })}
                    className="send-another-btn"
                  >
                    Send Another Message
                  </button>
                ) : (
                  <button 
                    onClick={() => setSubmitStatus({ submitted: false, success: false, message: '' })}
                    className="try-again-btn"
                  >
                    Try Again
                  </button>
                )}
              </div>
            ) : (
              <form className="contact-form" onSubmit={handleSubmit} noValidate>
                <div className="form-group">
                  <label htmlFor="name">
                    Name <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    aria-required="true"
                    aria-invalid={!!errors.name}
                    aria-describedby={errors.name ? 'name-error' : undefined}
                  />
                  {errors.name && (
                    <div className="error-message" id="name-error">{errors.name}</div>
                  )}
                </div>
                
                <div className="form-group">
                  <label htmlFor="email">
                    Email <span className="required">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    aria-required="true"
                    aria-invalid={!!errors.email}
                    aria-describedby={errors.email ? 'email-error' : undefined}
                  />
                  {errors.email && (
                    <div className="error-message" id="email-error">{errors.email}</div>
                  )}
                </div>
                
                <div className="form-group">
                  <label htmlFor="phone">Phone (Optional)</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="purpose">Purpose of Inquiry</label>
                  <select
                    id="purpose"
                    name="purpose"
                    value={formData.purpose}
                    onChange={handleChange}
                  >
                    <option value="">Please select...</option>
                    <option value="order">Place an Order</option>
                    <option value="catering">Catering Inquiry</option>
                    <option value="feedback">Feedback</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                
                <div className="form-group recaptcha-container">
                  {/* Accessibility label will be inserted by the useEffect */}
                  {/* The g-recaptcha div will be inserted by the useEffect */}
                  {errors.recaptcha && (
                    <div className="error-message" id="recaptcha-error">{errors.recaptcha}</div>
                  )}
                  <div className="recaptcha-note">This helps us prevent spam submissions</div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="message">
                    Message <span className="required">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={5}
                    value={formData.message}
                    onChange={handleChange}
                    aria-required="true"
                    aria-invalid={!!errors.message}
                    aria-describedby={errors.message ? 'message-error' : undefined}
                  ></textarea>
                  {errors.message && (
                    <div className="error-message" id="message-error">{errors.message}</div>
                  )}
                </div>
                
                <button type="submit" className="submit-button">
                  Send Message
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
